/**
 * ============================================================================
 * ⭐ "NO WMS (N)" — past visits with no WMS order (F107, Decision 284)
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"We need the same thing for No WMS"* → *"Past days, truck
 * > came"* · *"open our match screen with the information from the warning instead
 * > of the match information for that day? then they can see all non-matched
 * > appointments in a list and go down the list."* · *"The No WMS warning bubble …
 * > should include the day's appointments that don't have a WMS"* → *"The day on the
 * > board, all live"*.
 *
 * ⭐ THIS CHIP OWNS NO DIALOG. Clicking it hands the list to the page, which opens
 * the ordinary Match screen (`WmsOrderMatchDialog`) in its list mode: the unmatched
 * appointments on the left, the comparison and a WMS order search on the right.
 * One Match screen, reached two ways — attaching still goes through the page's
 * single attach path (A5, the replace popup included).
 *
 * ⭐ `refreshKey`: the page bumps it after every attach, so the count drops and an
 * open list loses the load that was just matched (`onRows`).
 * ⚠️ HIDDEN AT ZERO, like its siblings.
 */
import { useCallback, useEffect, useState } from 'react';
import { Unlink } from 'lucide-react';
import { OpenItemChip } from '@/components/OpenItemChip';
import type { NoWmsListDto, NoWmsRowDto } from '@/api/types';
import { listNoWmsAppointments } from '@/api/client';

const REFRESH_MS = 5 * 60 * 1000;

export function NoWmsChip({ facilityCode, fromDate, toDate, getToken, refreshKey = 0, onOpen, onRows }: {
    facilityCode: string | null;
    /** ⭐ The board's range (Day: both the same date) — its loads with no order are included. */
    fromDate: string;
    toDate: string;
    getToken: () => Promise<string>;
    refreshKey?: number;
    /** Open the Match screen on these loads. */
    onOpen: (rows: NoWmsRowDto[], total: number) => void;
    /** Every fresh read, so an open Match list stays current. */
    onRows?: (rows: NoWmsRowDto[], total: number) => void;
}) {
    const [data, setData] = useState<NoWmsListDto | null>(null);

    const load = useCallback(async (): Promise<NoWmsListDto | null> => {
        if (!facilityCode) { setData(null); return null; }
        try {
            const d = await listNoWmsAppointments(await getToken(), facilityCode, { from: fromDate, to: toDate });
            setData(d);
            onRows?.(d.rows, d.total);
            return d;
        } catch {
            return null;   // ⚠️ a failed background read keeps the last count rather than hiding the chip
        }
    }, [facilityCode, fromDate, toDate, getToken, onRows]);

    useEffect(() => {
        void load();
        const t = window.setInterval(() => { void load(); }, REFRESH_MS);
        const onFocus = () => { void load(); };
        window.addEventListener('focus', onFocus);
        return () => { window.clearInterval(t); window.removeEventListener('focus', onFocus); };
    }, [load, refreshKey]);

    const total = data?.total ?? 0;
    if (!facilityCode || total === 0) return null;

    return (
        <OpenItemChip
            onClick={() => {
                if (data) onOpen(data.rows, data.total);
                void load();   // fresh rows arrive through onRows
            }}
            title="Loads with no WMS order: past visits where the truck came, and this board's days — open the Match screen to find each one's order"
            icon={<Unlink className="h-3 w-3" aria-hidden="true" />}
        >
            No WMS ({total})
        </OpenItemChip>
    );
}
